import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, API_BASE, tokenStore } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import { formatDateTime, formatINR } from "@/lib/utils";

async function downloadInvoice(order) {
  try {
    const res = await fetch(`${API_BASE}/payments/orders/${order.id}/invoice.pdf`, {
      headers: { Authorization: `Bearer ${tokenStore.get()}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice_${order.invoice_no || order.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(`Failed to download invoice: ${err.message}`);
  }
}

export default function BillingPage() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/payments/orders").then(({ data }) => setOrders(data)); }, []);
  if (!orders) return <LoadingScreen />;
  return (
    <div className="sqit-card overflow-hidden">
      <div className="p-6 pb-4">
        <h3 className="font-display text-lg font-semibold text-gray-900">Purchase history</h3>
        <p className="text-xs text-gray-500 mt-1">Download an invoice for any paid purchase.</p>
      </div>
      {orders.length === 0 ? (
        <div className="p-8 text-center text-gray-500 border-t border-[#E5E7EB]">
          No purchases yet.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="billing-table">
          <thead className="bg-gray-50 border-y border-[#E5E7EB]">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Plan</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Credits</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">When</th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-gray-100">
                <td className="px-6 py-3 font-medium text-gray-900">{o.invoice_no || "—"}</td>
                <td className="px-6 py-3">{o.label}</td>
                <td className="px-6 py-3">{o.credits}</td>
                <td className="px-6 py-3">{formatINR(o.amount_inr)}</td>
                <td className="px-6 py-3 capitalize">{o.status}</td>
                <td className="px-6 py-3 text-xs text-gray-500">{formatDateTime(o.paid_at || o.created_at)}</td>
                <td className="px-6 py-3 text-right">
                  {o.status === "paid" ? (
                    <button
                      onClick={() => downloadInvoice(o)}
                      data-testid={`invoice-download-${o.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#0B6936]/30 px-3 py-1.5 text-xs font-semibold text-[#0B6936] transition-all duration-200 hover:border-[#0B6936] hover:bg-[#0B6936] hover:text-white"
                    >
                      <Download size={12} /> PDF
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
